import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, User, Phone, 
  Scissors, Check, X, Store, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate time slots from 9:00 to 20:00 (8 PM)
const TIME_SLOTS = Array.from({ length: 23 }, (_, i) => {
  const hour = Math.floor(i / 2) + 9;
  const minute = i % 2 === 0 ? '00' : '30';
  return `${hour.toString().padStart(2, '0')}:${minute}`;
});

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-500',
  confirmed: 'bg-green-500/20 border-green-500/50 text-green-500',
  completed: 'bg-zinc-500/20 border-zinc-500/50 text-zinc-400',
  cancelled: 'bg-red-500/20 border-red-500/50 text-red-500',
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

export default function AdminAppointments() {
  const { getAuthHeaders, user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all'); // 'all' for master_admin
  const [loading, setLoading] = useState(true);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    calculateWeekDates(currentDate);
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      fetchAppointments();
    }
  }, [selectedBranch, weekDates, branches]);

  const fetchInitialData = async () => {
    try {
      const branchesRes = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = branchesRes.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = branchesRes.data.filter(b => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      } else if (isMasterAdmin) {
        setSelectedBranch('all'); // Master admin sees all by default
      } else if (availableBranches.length > 0) {
        setSelectedBranch(availableBranches[0].id);
      }
      
      setBranches(availableBranches);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const fetchAppointments = async () => {
    if (!weekDates.length) return;
    
    setLoading(true);
    try {
      const startDate = weekDates[0].toISOString().split('T')[0];
      const endDate = weekDates[6].toISOString().split('T')[0];
      
      let allAppointments = [];
      
      if (selectedBranch === 'all' && isMasterAdmin) {
        // Fetch appointments from all branches
        const promises = branches.map(branch => 
          axios.get(`${API}/appointments?branch_id=${branch.id}`, getAuthHeaders())
        );
        const responses = await Promise.all(promises);
        responses.forEach((res, index) => {
          const branchAppointments = res.data.map(apt => ({
            ...apt,
            branch_name: branches[index].name,
            business_name: branches[index].business_name
          }));
          allAppointments = [...allAppointments, ...branchAppointments];
        });
      } else {
        const response = await axios.get(
          `${API}/appointments?branch_id=${selectedBranch}`,
          getAuthHeaders()
        );
        const branch = branches.find(b => b.id === selectedBranch);
        allAppointments = response.data.map(apt => ({
          ...apt,
          branch_name: branch?.name,
          business_name: branch?.business_name
        }));
      }
      
      // Filter by week dates
      const filtered = allAppointments.filter(apt => 
        apt.date >= startDate && apt.date <= endDate && apt.status !== 'cancelled'
      );
      setAppointments(filtered);
    } catch (error) {
      toast.error('Error al cargar citas');
    } finally {
      setLoading(false);
    }
  };

  const calculateWeekDates = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      dates.push(d);
    }
    setWeekDates(dates);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
    calculateWeekDates(newDate);
  };

  const getAppointmentsForSlot = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.date === dateStr && apt.time === time);
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.put(
        `${API}/appointments/${appointmentId}/status?status=${status}`,
        {},
        getAuthHeaders()
      );
      toast.success(`Cita ${STATUS_LABELS[status].toLowerCase()}`);
      fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  // Group branches by business for the selector
  const branchesByBusiness = branches.reduce((acc, branch) => {
    const biz = branch.business_name || 'Sin Negocio';
    if (!acc[biz]) acc[biz] = [];
    acc[biz].push(branch);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-appointments">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Calendario de Citas</h1>
            <p className="text-zinc-500 mt-1">
              {isMasterAdmin 
                ? 'Vista de todas las citas agendadas en todas las sucursales' 
                : 'Citas de tu sucursal'
              }
            </p>
          </div>
          
          {/* Branch Filter */}
          <div className="flex gap-3">
            <Select 
              value={selectedBranch} 
              onValueChange={setSelectedBranch}
              disabled={!isMasterAdmin && user?.branch_id}
            >
              <SelectTrigger className="w-[250px] bg-zinc-900 border-zinc-800 text-white">
                <Store className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="Filtrar por sucursal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                {isMasterAdmin && (
                  <SelectItem value="all" className="text-white font-medium">
                    📊 Todas las Sucursales
                  </SelectItem>
                )}
                {Object.entries(branchesByBusiness).map(([business, branchList]) => (
                  <div key={business}>
                    <div className="px-2 py-1 text-xs text-zinc-500 uppercase tracking-wider mt-2">
                      {business}
                    </div>
                    {branchList.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id} className="text-white pl-4">
                        {branch.name}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Summary for Master Admin */}
        {isMasterAdmin && selectedBranch === 'all' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800 p-4">
              <p className="text-2xl font-heading text-white">{appointments.length}</p>
              <p className="text-xs text-zinc-500">Citas esta semana</p>
            </Card>
            <Card className="bg-yellow-500/10 border-yellow-500/30 p-4">
              <p className="text-2xl font-heading text-yellow-500">
                {appointments.filter(a => a.status === 'pending').length}
              </p>
              <p className="text-xs text-zinc-500">Pendientes</p>
            </Card>
            <Card className="bg-green-500/10 border-green-500/30 p-4">
              <p className="text-2xl font-heading text-green-500">
                {appointments.filter(a => a.status === 'confirmed').length}
              </p>
              <p className="text-xs text-zinc-500">Confirmadas</p>
            </Card>
            <Card className="bg-zinc-700/50 border-zinc-600 p-4">
              <p className="text-2xl font-heading text-zinc-400">
                {appointments.filter(a => a.status === 'completed').length}
              </p>
              <p className="text-xs text-zinc-500">Completadas</p>
            </Card>
          </div>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigateWeek(-1)}
            className="text-zinc-400 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h2 className="font-heading text-xl text-white">
            {weekDates[0] && `${weekDates[0].getDate()} - ${weekDates[6]?.getDate()} de ${monthNames[weekDates[0].getMonth()]} ${weekDates[0].getFullYear()}`}
          </h2>
          <Button
            variant="ghost"
            onClick={() => navigateWeek(1)}
            className="text-zinc-400 hover:text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
          <div className="grid grid-cols-8 border-b border-zinc-800">
            <div className="p-3 text-center text-xs text-zinc-500 uppercase tracking-widest">
              Hora
            </div>
            {weekDates.map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString();
              return (
                <div
                  key={date.toISOString()}
                  className={`p-3 text-center border-l border-zinc-800 ${
                    isToday ? 'bg-gold/10' : ''
                  }`}
                >
                  <p className="text-xs text-zinc-500 uppercase">{dayNames[index]}</p>
                  <p className={`text-lg font-semibold ${isToday ? 'text-gold' : 'text-white'}`}>
                    {date.getDate()}
                  </p>
                </div>
              );
            })}
          </div>

          <ScrollArea className="h-[500px]">
            {TIME_SLOTS.map((time) => (
              <div key={time} className="grid grid-cols-8 border-b border-zinc-800/50">
                <div className="p-2 text-center text-xs text-zinc-500 flex items-center justify-center">
                  {time}
                </div>
                {weekDates.map((date, dayIndex) => {
                  const slotAppointments = getAppointmentsForSlot(date, time);
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div
                      key={date.toISOString()}
                      className={`p-1 border-l border-zinc-800/50 min-h-[60px] ${
                        isToday ? 'bg-gold/5' : ''
                      }`}
                    >
                      {slotAppointments.map((apt) => (
                        <button
                          key={apt.id}
                          onClick={() => setSelectedAppointment(apt)}
                          className={`w-full p-2 rounded-sm text-left text-xs border ${STATUS_COLORS[apt.status]} mb-1 hover:opacity-80 transition-opacity`}
                        >
                          <p className="font-medium truncate">{apt.client_name}</p>
                          {selectedBranch === 'all' && apt.branch_name && (
                            <p className="text-[10px] opacity-70 truncate flex items-center gap-1">
                              <MapPin className="w-2 h-2" />
                              {apt.branch_name}
                            </p>
                          )}
                          {apt.barber_name && (
                            <p className="text-[10px] opacity-70 truncate">{apt.barber_name}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </ScrollArea>
        </Card>

        {/* Info Note */}
        <Card className="bg-blue-500/10 border-blue-500/30 p-4">
          <p className="text-sm text-blue-400">
            <strong>Nota:</strong> Las citas se agendan a través del asistente virtual en la página pública de citas (/citas). 
            Aquí puedes ver y gestionar el estado de las citas existentes.
          </p>
        </Card>

        {/* Appointment Detail Dialog */}
        <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white">
                Detalle de Cita
              </DialogTitle>
            </DialogHeader>
            {selectedAppointment && (
              <div className="space-y-4 mt-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-zinc-500" />
                    <span className="text-white">{selectedAppointment.client_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-zinc-500" />
                    <span className="text-white">{selectedAppointment.client_whatsapp}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-zinc-500" />
                    <span className="text-white">{selectedAppointment.date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-zinc-500" />
                    <span className="text-white">{selectedAppointment.time}</span>
                  </div>
                  {selectedAppointment.branch_name && (
                    <div className="flex items-center gap-3">
                      <Store className="w-4 h-4 text-zinc-500" />
                      <span className="text-white">
                        {selectedAppointment.business_name && `${selectedAppointment.business_name} - `}
                        {selectedAppointment.branch_name}
                      </span>
                    </div>
                  )}
                  {selectedAppointment.barber_name && (
                    <div className="flex items-center gap-3">
                      <Scissors className="w-4 h-4 text-zinc-500" />
                      <span className="text-white">{selectedAppointment.barber_name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">Estado:</span>
                  <span className={`px-2 py-1 rounded-sm text-xs ${STATUS_COLORS[selectedAppointment.status]}`}>
                    {STATUS_LABELS[selectedAppointment.status]}
                  </span>
                </div>

                <div className="flex gap-2 pt-4 border-t border-zinc-800">
                  {selectedAppointment.status === 'pending' && (
                    <Button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'confirmed')}
                      className="flex-1 bg-green-500/20 text-green-500 hover:bg-green-500/30"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Confirmar
                    </Button>
                  )}
                  {selectedAppointment.status === 'confirmed' && (
                    <Button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'completed')}
                      className="flex-1 bg-zinc-700 text-white hover:bg-zinc-600"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Completar
                    </Button>
                  )}
                  {selectedAppointment.status !== 'cancelled' && selectedAppointment.status !== 'completed' && (
                    <Button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'cancelled')}
                      variant="outline"
                      className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
